import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { usePermissions } from '../context/PermissionContext';
import { MONTHLY_COLLECTIONS_PERMISSIONS } from '../constants/reports';
import { getMonthlyCollections, getDischargeRegister } from '../services/api/monthlyReportsApi';
import AdminPageHeader from '../components/common/AdminPageHeader';
import IpdRevenue from '../components/reports/IpdRevenue';
import DischargeRegister from '../components/reports/DischargeRegister';
import ByDoctorTable from '../components/reports/ByDoctorTable';
import ReportArchiveCard from '../components/reports/ReportArchiveCard';
import CashCollectionsTab from '../components/reports/CashCollectionsTab';
import DoctorPicker from '../components/procedures/DoctorPicker';
import {
  selectDoctorBuckets,
  selectUnifiedDoctorFilterOptions,
  selectShowPaidAfterDischarge,
} from '../components/reports/monthlyReportModel';

// DOCTOR-REPORTS P2 (FE-4, build-plan D8) — mockup variant A puts the new
// cash tab 3rd; existing IPD tabs are relabelled "IPD …" so the two money
// bases (billed-at-discharge vs collected) are never read as one total.
const TABS = [
  { id: 'summary', label: 'IPD summary' },
  { id: 'bydoctor', label: 'IPD by doctor' },
  { id: 'opdotclab', label: 'OPD, OTC, Lab & Diagnostic' },
  { id: 'register', label: 'IPD register' },
];

// Same small inline spinner pattern already used elsewhere in this repo
// (ManageVisitPage's `MiniSpinner`, RegisterVisitForm's `Spinner`) — kept
// local rather than re-exported so this page doesn't reach across domains
// for a one-off SVG.
function Spinner() {
  return (
    <svg className="animate-spin" style={{ width: '1rem', height: '1rem' }} viewBox="0 0 24 24" fill="none">
      <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

/**
 * MonthlyCollectionsPage (S2 — IPD-MVP discharge-mis, BUILD-PLAN
 * fast-slice-v2 §8, WAVE 0). The "Collections" card's page shell: month
 * picker + the as-at stamp + the empty state + a minimal discharge register.
 *
 * - Gated on VIEW_MONTHLY_COLLECTIONS ONLY — never `useAdmissionEnabled()`,
 *   never a role. An OPD-only org (module off) reaches this page too (§2.4).
 * - `as_of_ist_display` is printed VERBATIM — no client-side tz math.
 * - The month picker is a native `<input type="month">` — a single
 *   calendar month is the only value it can ever express.
 * - A data block (`ipd`, and `opd` from Increment 2) renders IFF its key is
 *   present on the response — never tested for null/zero. When NEITHER
 *   block is present the page renders the plain empty state (§2.4) instead
 *   of a broken-looking IPD section — this is what keeps an OPD-only org
 *   from seeing zeros it doesn't have.
 * - The discharge register is fetched ONLY when `ipd` is present (the
 *   server 403s `IPD_MODULE_DISABLED` otherwise, §7.2) — single-flight,
 *   bounded to one request per month.
 * - The full register (money + pagination + the unreconciled flag, F4) is
 *   `DischargeRegister` — this page owns only the fetch/wiring.
 *
 * DOCTOR-REPORTS P1 (FE-6) — variant-B tabs (Summary / By doctor / Register)
 * + a Doctor filter above them that drives all three off ONE selected-key
 * state. The filter is CLIENT-SIDE ONLY (D7 — options come from
 * `ipd.by_doctor`, never a members call) and is deliberately NOT in any
 * fetch-effect's dependency array, so switching doctors or tabs fires ZERO
 * requests. `selectedDoctorKey` resets to null on every month change
 * (BEFORE the new board fetch) and the tab state is local, never in the URL.
 *
 * DOCTOR-REPORTS P2 (FE-4, build-plan §API CONTRACT FROZEN, D1/D8/D9) — the
 * SAME single board fetch additionally carries `opd`/`otc`/`lab`/`radiology`/
 * `cash_coverage` (D1: one fetch, no second request, no per-tab skeleton).
 * A 3rd tab "OPD, OTC, Lab & Diagnostic" (D8; OR-47) renders whenever `opd`
 * is present — an OPD-only org (`!hasIpd && hasOpd`) hides the three IPD tabs and defaults
 * onto it; an old backend without the cash keys hides it and the page is
 * byte-identical to P1. The Doctor filter is now the UNION of IPD + cash
 * doctors (`selectUnifiedDoctorFilterOptions`, D9) — selecting a doctor
 * with cash money but no discharges shows "No discharges for {doctor} in
 * {month}." on the IPD summary tab instead of silently falling back to the
 * organisation total.
 */
export default function MonthlyCollectionsPage() {
  const { hasPermission } = usePermissions();
  const canView = hasPermission(MONTHLY_COLLECTIONS_PERMISSIONS.VIEW_MONTHLY_COLLECTIONS);

  const [month, setMonth] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorInfo, setErrorInfo] = useState(null);
  const [register, setRegister] = useState(null);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState(null);
  const [selectedDoctorKey, setSelectedDoctorKey] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');

  const mountedFetchRef = useRef(false);

  // ── Board fetch — single-flight. `m` undefined ⇒ server picks the
  //    default (last full month); the response's own `period.month` then
  //    seeds the picker so the FE never computes "last month" itself. ────
  const fetchBoard = useCallback((m) => {
    setLoading(true);
    setErrorInfo(null);
    getMonthlyCollections(m).then((res) => {
      setLoading(false);
      if (res.success) {
        setData(res.data);
        if (res.data?.period?.month) setMonth(res.data.period.month);
      } else {
        setData(null);
        setErrorInfo(res);
      }
    });
  }, []);

  // Mount: exactly ONE board fetch, gated on the permission being held.
  useEffect(() => {
    if (!canView || mountedFetchRef.current) return;
    mountedFetchRef.current = true;
    fetchBoard();
  }, [canView, fetchBoard]);

  const handleMonthChange = (e) => {
    const v = e.target.value;
    if (!v) return;
    setSelectedDoctorKey(null);
    setMonth(v);
    fetchBoard(v);
  };

  // A refetch (month/filter-driven re-fetch of the SAME board endpoint)
  // while the previous month's figures are still on screen — distinct from
  // the very first load (`loading && !data`, handled by the plain "Loading…"
  // state below). Never let the old month's numbers sit there un-flagged
  // while a different month is in flight (owner rule: no stale-as-current).
  const isRefetching = loading && !!data;

  const hasIpd = !!(data && Object.prototype.hasOwnProperty.call(data, 'ipd'));
  const hasOpd = !!(data && Object.prototype.hasOwnProperty.call(data, 'opd'));
  const hasAnyBlock = hasIpd || hasOpd;
  const activeMonth = data?.period?.month;

  // Doctor filter — CLIENT-SIDE ONLY (D7), never an input to a fetch effect.
  // P2 (D9): the UNION of IPD + cash (OPD/OTC-doctor) doctors — identical to
  // P1's `selectDoctorFilterOptions(data.ipd)` when `opd`/`otc` are absent.
  const hasDoctorBreakdown = hasIpd && Array.isArray(data.ipd?.by_doctor) && data.ipd?.discharges?.total > 0;
  const doctorFilterOptions = useMemo(
    () => (data ? selectUnifiedDoctorFilterOptions(data) : []),
    [data],
  );
  const filterKeys = useMemo(
    () => new Set(doctorFilterOptions.map((o) => o.member_id)),
    [doctorFilterOptions],
  );
  const doctorBuckets = useMemo(
    () => (hasIpd ? selectDoctorBuckets(data.ipd) : []),
    [hasIpd, data],
  );
  const selectedBucket = doctorBuckets.find((b) => b.key === selectedDoctorKey) || null;
  const selectedFilterOption = doctorFilterOptions.find((o) => o.member_id === selectedDoctorKey) || null;
  const selectedLabel = selectedFilterOption?.first_name || null;
  const showPaidAfterDischarge = hasIpd ? selectShowPaidAfterDischarge(data.ipd) : false;
  // A doctor selected via the unified filter (cash-only, e.g. from OPD) who
  // has zero IPD discharges this month — the Summary tab states that
  // plainly instead of silently falling back to the organisation total.
  const ipdFilteredNoDischarges = hasIpd && !!selectedDoctorKey && !selectedBucket && !!selectedLabel;

  const handleDoctorFilterChange = (key) => setSelectedDoctorKey(key || null);
  // A row is only a filter when the picker can show it (unified options) —
  // e.g. the always-present IPD Unassigned row at 0 discharges (D2) is not
  // an option, so selecting it would leave the picker reading "All doctors"
  // while the page is filtered.
  const handleByDoctorSelect = (key) => setSelectedDoctorKey(
    key && filterKeys.has(key) ? key : null,
  );

  // Every tab folds through the SAME availability rule, so a stale
  // `activeTab` can never strand the page on a tab that no longer exists
  // (old backend, an OPD-only org with no IPD tabs, or a month that flips
  // to 0 discharges). Falls back to the first tab that IS available.
  const TAB_AVAILABLE = {
    summary: hasIpd,
    bydoctor: hasDoctorBreakdown,
    opdotclab: hasOpd,
    register: hasIpd,
  };
  const availableTabIds = TABS.filter((t) => TAB_AVAILABLE[t.id]).map((t) => t.id);
  const effectiveTab = availableTabIds.includes(activeTab) ? activeTab : (availableTabIds[0] || 'summary');

  // Discharge register — single-flight per (hasIpd, activeMonth) pair.
  // Never called while `ipd` is absent (§7.2 — the FE never links to it).
  useEffect(() => {
    if (!hasIpd || !activeMonth) {
      setRegister(null);
      setRegisterError(null);
      return undefined;
    }
    let alive = true;
    setRegisterLoading(true);
    setRegisterError(null);
    getDischargeRegister(activeMonth).then((res) => {
      if (!alive) return;
      setRegisterLoading(false);
      setRegister(res.success ? res.data : null);
      // A failed register fetch must SAY so — an empty register card is
      // otherwise indistinguishable from "no discharges this month", which
      // an accountant would sign off on. The message is the server's own
      // (parsed by monthlyReportsApi), never dev-written here.
      setRegisterError(res.success ? null : (res.error || null));
    });
    return () => { alive = false; };
  }, [hasIpd, activeMonth]);

  if (!canView) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <AdminPageHeader />
        <div className="mx-auto max-w-3xl p-6">
          <div role="alert" data-testid="no-access" className="rounded-xl border border-amber-300 bg-amber-50 p-5 text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100">
            <h2 className="mb-1 text-lg font-semibold">Reports &amp; Accounts</h2>
            <p className="text-sm">You do not have access to Reports &amp; Accounts. Ask your organisation&apos;s administrator to give you access.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <AdminPageHeader />
      <div className="mx-auto max-w-5xl p-4" data-testid="monthly-collections-page">
        <h1 className="mb-3 text-xl font-semibold text-slate-900 dark:text-slate-50">Reports &amp; Accounts</h1>

        {/* BUSY-VOUCHER R3 (FE-PAGE3, OR-28/OR-35/OR-80) — "Monthly files" is
            its own section, ABOVE and OUTSIDE the Doctor + Month filters. It is
            org-wide, takes no props and fetches once on its own mount, so
            changing the month or the doctor below can never reach it or
            re-fetch it. Self-gated (DOWNLOAD_BUSY_FILES); a failure in
            it must never break the board below. */}
        <ReportArchiveCard />

        <div data-testid="collections-board-head" className="mb-2 flex flex-wrap items-center justify-between gap-3 pt-1">
          <h2 className="text-base font-bold text-slate-900 dark:text-slate-50">Collections board</h2>
          <div className="flex flex-wrap items-center gap-3">
            {doctorFilterOptions.length > 0 && (
              // A <div>, not a <label>: a label wrapping the whole picker
              // re-dispatches a click to its first control (the trigger)
              // whenever a non-interactive part of the open popup is
              // clicked, toggling the popup shut.
              <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200" data-testid="doctor-filter">
                <span>Doctor</span>
                <DoctorPicker
                  doctors={doctorFilterOptions}
                  selectedId={selectedDoctorKey}
                  onChange={handleDoctorFilterChange}
                  isLoading={loading}
                  placeholder="All doctors"
                  searchPlaceholder="Search doctors…"
                  clearLabel="Show all doctors"
                  showSubline={false}
                />
              </div>
            )}
            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
              Month
              <input
                type="month"
                aria-label="Select month"
                data-testid="month-picker"
                value={month}
                onChange={handleMonthChange}
                className="rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              />
            </label>
          </div>
        </div>

        {loading && !data && (
          <div data-testid="loading" className="p-6 text-center text-sm text-slate-500">Loading…</div>
        )}

        {isRefetching && (
          <div
            data-testid="board-refetching"
            role="status"
            aria-live="polite"
            className="mb-3 flex items-center gap-2 rounded-lg border border-teal-300 bg-teal-50 p-2.5 text-sm text-teal-800 dark:border-teal-700 dark:bg-teal-900/30 dark:text-teal-100"
          >
            <Spinner />
            Loading figures…
          </div>
        )}

        {errorInfo && (
          <div role="alert" data-testid="error-panel" className="mb-3 rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-700 dark:bg-rose-900/30 dark:text-rose-100">
            {errorInfo.error}
          </div>
        )}

        {data && (
          <div
            data-testid="board-content"
            aria-busy={isRefetching}
            className={isRefetching ? 'pointer-events-none opacity-50 transition-opacity' : ''}
          >
            {/* ExportMonthButton ("Download Excel", the in-app monthly
                export) removed at the owner's instruction, 2026-08-19 — not
                required for now. Component untouched, just unmounted here. */}
            <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
              {data.period?.label && <span>{data.period.label}</span>}
              <span data-testid="as-of">Figures as at {data.as_of_ist_display}</span>
            </div>

            {!hasAnyBlock && (
              <div
                data-testid="empty-state"
                className="rounded-xl border border-slate-200 bg-white p-6 text-center dark:border-slate-700 dark:bg-slate-900"
              >
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Nothing to report for {data.period?.label}. This report covers in-patient (IPD) revenue,
                  and the in-patient module is not enabled for this organisation. Your administrator can
                  enable it.
                </p>
              </div>
            )}

            {hasAnyBlock && (
              <>
                {/* Variant B (owner GATE 1, g218) — inline tablist above the
                    board, driven by the SAME `selectedDoctorKey` as the
                    Doctor filter and the DoctorPicker above. Tab state is
                    local only, never persisted/URL-synced. Each tab is
                    omitted (never rendered) when `TAB_AVAILABLE` says so —
                    old backend / zero-discharge month / OPD-only org (no
                    IPD tabs at all, P2 D8/AC5). */}
                <div role="tablist" aria-label="Reports and Accounts sections" className="mb-3 flex flex-wrap gap-1 border-b border-slate-200 dark:border-slate-700">
                  {TABS.filter((t) => TAB_AVAILABLE[t.id]).map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      role="tab"
                      aria-selected={effectiveTab === t.id}
                      data-testid={`tab-${t.id}`}
                      onClick={() => setActiveTab(t.id)}
                      // Repo transparency rule #1: inactive tabs carry a solid
                      // light + dark background, never bg-transparent.
                      className={`-mb-px rounded-t-md border-b-2 px-3 py-2 text-sm font-semibold ${
                        effectiveTab === t.id
                          ? 'border-teal-700 bg-teal-600 text-white'
                          : 'border-slate-300 bg-white text-slate-800 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* All three panels stay MOUNTED (never re-fetched on a tab
                    switch) — only the inactive ones are visually hidden via
                    the native `hidden` attribute. This keeps each panel's
                    own local state (the register's page number, the
                    by-doctor search box) intact across tab switches, and
                    keeps tab-switching a zero-request, zero-refetch action. */}
                {hasIpd && (
                  <div role="tabpanel" hidden={effectiveTab !== 'summary'} className="mb-3">
                    {ipdFilteredNoDischarges ? (
                      <div
                        data-testid="ipd-filtered-no-discharges"
                        className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                      >
                        No discharges for {selectedLabel} in {data.period?.label}.
                      </div>
                    ) : (
                      <IpdRevenue data={data} doctorBucket={selectedBucket} showPaidAfterDischarge={showPaidAfterDischarge} hasOpd={hasOpd} />
                    )}
                  </div>
                )}

                {hasDoctorBreakdown && (
                  <div role="tabpanel" hidden={effectiveTab !== 'bydoctor'} className="mb-3">
                    <ByDoctorTable
                      data={data}
                      selectedKey={selectedDoctorKey}
                      onSelect={handleByDoctorSelect}
                      showPaidAfterDischarge={showPaidAfterDischarge}
                    />
                  </div>
                )}

                {hasOpd && (
                  <div role="tabpanel" hidden={effectiveTab !== 'opdotclab'} className="mb-3">
                    <CashCollectionsTab
                      data={data}
                      selectedKey={selectedDoctorKey}
                      selectedLabel={selectedLabel}
                      filterKeys={filterKeys}
                      onSelectKey={handleByDoctorSelect}
                    />
                  </div>
                )}

                {hasIpd && (
                  <div role="tabpanel" hidden={effectiveTab !== 'register'}>
                    <DischargeRegister
                      register={register}
                      loading={registerLoading}
                      error={registerError}
                      doctorKey={selectedDoctorKey}
                      doctorLabelText={selectedLabel}
                      showPaidAfterDischarge={showPaidAfterDischarge}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
